import { isDefined } from "@clipboard-health/util-ts";

import {
  useGetWorkerDetailsAtWorkplace,
  WorkerCategory,
} from "../../Professionals/api/useGetWorkerDetailsAtWorkplace";
import { useWorker } from "../../Worker/api/useWorker";
import { ChartingCredentialSoftware } from "../api/schema";
import { useChartingCredentialProvisioningConfig } from "../api/useCredentialProvisioningConfig";
import { useGetWorkerCredentialProvisionRequests } from "../api/useGetWorkerCredentialProvisionRequests";
import { useGetWorkerCredentials } from "../api/useGetWorkerCredentials";
import { calculateScheduledProvisionTime } from "../utils/calculateScheduledProvisionTime";
import {
  type CredentialAccountStatus,
  determineCredentialAccountStatus,
} from "../utils/credentialAccountStatusHelpers";

interface UseCredentialAccountDataParams {
  workerId: string;
  workplaceId: string;
}

interface UseCredentialAccountDataResult {
  accountStatus: CredentialAccountStatus;
  defaultQualification: string | undefined;
  isError: boolean;
  isLoading: boolean;
  scheduledTimeText: string | undefined;
  username: string | undefined;
}

export function useCredentialAccountData(
  params: UseCredentialAccountDataParams
): UseCredentialAccountDataResult {
  const { workerId, workplaceId } = params;

  const provisioningConfig = useChartingCredentialProvisioningConfig();
  const isFeatureEnabled = provisioningConfig.enabled;

  const {
    data: workerCredentialsResponse,
    isError: isErrorCredentials,
    isLoading: isLoadingCredentials,
  } = useGetWorkerCredentials({
    filter: { workerIds: workerId, workplaceId },
  });

  const {
    data: provisionRequests,
    isError: isErrorProvisionRequests,
    isLoading: isLoadingProvisionRequests,
  } = useGetWorkerCredentialProvisionRequests({ workerId, workplaceId });

  const {
    data: workerDetailsResponse,
    isError: isErrorWorkerDetails,
    isLoading: isLoadingWorkerDetails,
  } = useGetWorkerDetailsAtWorkplace({
    category: WorkerCategory.ALL_WORKERS,
    facilityId: workplaceId,
    pageSize: 1,
    workerIds: [workerId],
  });

  const { data: worker, isError: isErrorWorker, isLoading: isLoadingWorker } = useWorker(workerId);

  const mostRecentRequest = provisionRequests?.data?.[0];
  const mostRecentRequestStatus = mostRecentRequest?.attributes?.status;
  const mostRecentRequestType = mostRecentRequest?.attributes?.requestType;
  const credentialsExpiresAt = mostRecentRequest?.attributes?.credentialsExpiresAt;
  const hasExistingProvisionerAccount =
    provisionRequests?.data.some((request) => request.attributes.status === "succeeded") ?? false;

  const { shifts } = workerDetailsResponse?.workers[0] ?? {};
  const { lastShift, nextShift } = shifts ?? {};
  const hasNextShift = isDefined(nextShift?.start);

  const { matchingFacilityQualifications, qualification: primaryQualification } = worker ?? {};
  const defaultQualification =
    nextShift?.agentReq ??
    lastShift?.agentReq ??
    matchingFacilityQualifications?.find(
      (qualification) => qualification === primaryQualification
    ) ??
    matchingFacilityQualifications?.[0];

  const isLoading =
    isLoadingCredentials || isLoadingProvisionRequests || isLoadingWorkerDetails || isLoadingWorker;
  const isError =
    isErrorCredentials || isErrorProvisionRequests || isErrorWorkerDetails || isErrorWorker;

  const pccCredential = workerCredentialsResponse?.data.find(
    (credential) =>
      credential.attributes.chartingCredentials?.software ===
      ChartingCredentialSoftware.POINT_CLICK_CARE
  );
  const { username } = pccCredential?.attributes.chartingCredentials ?? {};

  const accountStatus = determineCredentialAccountStatus({
    hasExistingProvisionerAccount,
    hasUpcomingShiftStartingSoon: hasNextShift,
    isFeatureEnabled,
    mostRecentRequestStatus,
    mostRecentRequestType,
    credentialsExpiresAt,
  });

  let scheduledTimeText: string | undefined;
  if (
    accountStatus === "scheduled" &&
    isDefined(nextShift?.start) &&
    provisioningConfig.enabled &&
    isDefined(provisioningConfig.autoProvisionBeforeShiftThresholdInHours)
  ) {
    const { displayText } = calculateScheduledProvisionTime({
      autoProvisionBeforeShiftThresholdInHours:
        provisioningConfig.autoProvisionBeforeShiftThresholdInHours,
      nextShiftStart: nextShift.start,
    });
    scheduledTimeText = displayText;
  }

  return {
    accountStatus,
    defaultQualification,
    isError,
    isLoading,
    scheduledTimeText,
    username,
  };
}
